import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Icon } from './Icon';
import type { NombreIcono } from './iconos';
import estilos from './Button.module.css';

export type VarianteBoton = 'primario' | 'secundario' | 'fantasma' | 'peligro' | 'exito';
export type TamanoBoton = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children?: ReactNode;
  variante?: VarianteBoton;
  tamano?: TamanoBoton;
  /** Icono a la izquierda del texto. */
  icono?: NombreIcono;
  iconoDerecha?: NombreIcono;
  cargando?: boolean;
  anchoCompleto?: boolean;
  /**
   * Explicación de por qué el botón está deshabilitado.
   * Se muestra como tooltip para que el usuario nunca se quede sin saber
   * por qué no puede hacer algo.
   */
  motivoDeshabilitado?: string | null;
  /**
   * En móvil deja sólo el icono y esconde el texto.
   *
   * Para acciones cuyo icono ya se entiende sin leer —actualizar, añadir— en
   * una cabecera donde el texto obliga a apilar los botones y se come la
   * pantalla. El texto sigue en el DOM como etiqueta accesible, así que quien
   * navegue con lector de pantalla lo sigue oyendo.
   */
  compactoEnMovil?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variante = 'primario',
    tamano = 'md',
    icono,
    iconoDerecha,
    cargando = false,
    anchoCompleto = false,
    motivoDeshabilitado,
    compactoEnMovil = false,
    disabled,
    className,
    title,
    type = 'button',
    ...resto
  },
  ref,
) {
  const inhabilitado = disabled || cargando || Boolean(motivoDeshabilitado);
  const soloIcono = Boolean(icono) && !children;
  const tamanoIcono = tamano === 'lg' ? 20 : tamano === 'sm' ? 16 : 18;

  return (
    <button
      ref={ref}
      type={type}
      disabled={inhabilitado}
      title={motivoDeshabilitado ?? title}
      aria-busy={cargando || undefined}
      aria-disabled={inhabilitado || undefined}
      className={[
        estilos.boton,
        estilos[variante],
        estilos[tamano],
        anchoCompleto ? estilos.completo : '',
        soloIcono ? estilos.soloIcono : '',
        compactoEnMovil && icono ? estilos.compactoEnMovil : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...resto}
    >
      {cargando ? (
        <span className={estilos.cargador} aria-hidden />
      ) : (
        icono && <Icon nombre={icono} tamano={tamanoIcono} />
      )}
      {compactoEnMovil && icono ? (
        <span className={estilos.texto} data-texto>
          {children}
        </span>
      ) : (
        children
      )}
      {iconoDerecha && !cargando && <Icon nombre={iconoDerecha} tamano={tamanoIcono} />}
    </button>
  );
});
